import { forwardRef, useCallback, useEffect, useMemo, useRef, useState, useImperativeHandle, ForwardedRef, ChangeEvent, ReactNode } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import moment from 'moment';
import Dropzone from 'react-dropzone';
import { Row, Col } from 'react-bootstrap';
import {
  DOCUMENT_PICKER_PAGE_SIZE,
  getDocumentsByLocation,
  matchesAcceptedExtensions,
  parseAcceptedExtensions,
} from '../../Pages/AssetLibrary/Assets/store/asset.service';
import { useAssetPreviewUrl } from '../../Pages/AssetLibrary/Assets/useAssetPreviewUrl';
import { isImageMimeType } from '../../Pages/AssetLibrary/Assets/assetPreview';
import { NewInput, NewDatePicker, NewCheckbox, NewSelectMultiSelect } from '../PIM/common';
import { FileIcon } from '../../bombiix-icon';
import './UploadDocumentsAssetStyle.css';
import { logError } from '../../utils/logger';

const PDF_TITLE_MAX_LENGTH = 100;

function trimPdfTitle(value: string | null | undefined): string {
  if (value == null || value === '') {
    return '';
  }
  const title = String(value);
  return title.length > PDF_TITLE_MAX_LENGTH ? title.slice(0, PDF_TITLE_MAX_LENGTH) : title;
}

function isTodayOrFutureDate(date: Date | string): boolean {
  const today = moment(new Date());
  const target = moment(new Date(date));
  return target.diff(today, 'days') > -1;
}

/**
 * Asset-library "Locations Used" label the picker filters on for a host module. It must be one of
 * the library's own display labels (the server's ASSET_LOCATION_LABELS, mirrored by the Locations
 * Used picker list) — a label outside that set matches no asset, so every asset already filed under
 * the location drops out and only never-filed ones survive the includeUnassigned relaxation.
 */
function getLocationsUsedForModule(module: string | undefined): string | undefined {
  if (module === 'testing') return 'Testing';
  if (module === 'specificationDoc') return 'Specifications';
  if (module === 'careLabel') return 'Care Label';
  return undefined;
}

function computeShowChangeStatusCheckbox(status: string | (string | null)[] | null | undefined): boolean {
  if (!status || (Array.isArray(status) && isEmpty(status))) return false;
  if (Array.isArray(status)) {
    const pending = status.filter((value) => value === 'Pending' || value === 'pending');
    return isEmpty(pending);
  }
  return status !== 'Pending' && status !== 'pending';
}

export interface DocumentTypeOption {
  _id: string;
  name: string;
  isActive?: boolean;
}

interface Asset {
  _id: string;
  title?: string;
  description?: string | null;
  fileUrl?: string;
  fileType?: string;
  size?: number;
}

interface DocumentSearchResponse {
  data?: {
    assets?: Asset[];
    total?: number;
  };
}

/** react-select-style grading group option; `_id` is set to `value` once loaded from Redux. */
interface GradingGroupOption {
  _id?: string;
  value: string;
  label: string;
  [key: string]: unknown;
}

interface ProductDetailsState {
  gradingGroups?: GradingGroupOption[];
  [key: string]: unknown;
}

interface BOMConfigDetailsState {
  productDetails?: ProductDetailsState;
  [key: string]: unknown;
}

interface UploadDocumentsAssetStyleRootState {
  BOMConfigDetails: BOMConfigDetailsState;
}

/**
 * Reference to a document selected directly from the Asset Library. Only `id` is sent to the
 * server — it resolves the S3 key/mimetype itself so a client can never link an S3 key it
 * doesn't have DB-verified access to. `key`/`mimetype` are kept client-side for the preview only.
 */
interface ExistingAssetRef {
  id: string;
  key: string;
  mimetype: string;
  fileName: string;
  size?: number;
}

interface SupportingDocumentsState {
  fileName: string;
  fileExtn: string;
  document: File | null;
  existingAsset: ExistingAssetRef | null;
  documentType: DocumentTypeOption | null;
  expairyDueDate: Date | null;
  changeStatus: boolean;
  addToDesign: boolean;
  /**
   * True when the "Add to Asset Library" tick was set alongside an uploaded file. Only ever true
   * when the host passed `showAddToAssetLibrary` and the file came from the device: a file picked
   * from the library is already an asset. Callers forward it to their save payload as
   * `isAddToAssetLibrary`, which is what makes the backend store the upload under the Asset Library
   * prefix and file it as an asset instead of keeping it on the module's own prefix.
   */
  addToAssetLibrary: boolean;
}

interface ErrorState {
  fileName: string;
  document: string;
  sizeError: string;
  typeError: string;
  documentType: string;
  expairyDueDate: string;
}

export interface UploadDocumentsAssetStyleProps {
  handleUpload?: () => void;
  module?: string;
  fromLibrary?: boolean;
  onSelectedGradingGroupChange: (selected: unknown[]) => void;
  showGradingGroupError?: boolean;
  setShowGradingGroupError: (value: boolean) => void;
  fromGrading?: boolean;
  documentTypeList?: DocumentTypeOption[];
  hideName?: boolean;
  accept?: string;
  productId?: string | null;
  /**
   * Override the asset fetch. When provided, productId is ignored. Called once per page, with the
   * same accept list the default fetch forwards so the override can filter identically.
   */
  fetchDocuments?: (search: string, page: number, limit: number, accept?: string) => Promise<DocumentSearchResponse>;
  /** Hides the dropzone + asset-search UI, for metadata-only edit flows (module="testing"). Defaults to true. */
  showDropZone?: boolean;
  isRenewMode?: boolean;
  /** Prefill for the expiry date field (module="testing"). */
  expairyDate?: string | Date | null;
  /** Prefill for the title field (module="testing"). */
  renewName?: string;
  /** Drives "Clear status" checkbox visibility (module="testing"). */
  status?: string | (string | null)[] | null;
  isSupplierLoggedIn?: boolean;
  isProductArchived?: boolean;
  /** Per-file validation (MIME type, extension, path-traversal, etc). Overrides the default size-only check when provided. */
  validateFile?: (file: File) => { valid: boolean; errors: string[] };
  /**
   * Module-specific fields rendered in the bottom action bar, beside the document title, so a
   * caller's own inputs sit on the same row as the title instead of below the uploader.
   */
  bottomBarContent?: ReactNode;
  /**
   * Renders the "Add to Asset Library" checkbox under the bottom bar. Off by default so the hosts
   * whose save endpoint does not understand `isAddToAssetLibrary` are unaffected.
   */
  showAddToAssetLibrary?: boolean;
  /** Editing an existing document (module="designSupportingDoc") — prefills fields and makes re-upload optional. */
  editId?: string | null;
  editData?: { documentName?: string; addToDesign?: boolean; expiredOn?: string | Date | null } | null;
}

export interface UploadDocumentsAssetStyleHandle {
  selectedDocument: () => SupportingDocumentsState | undefined;
}

interface AssetThumbProps {
  asset: Asset;
  isSelected: boolean;
  onSelect: (asset: Asset) => void;
}

/**
 * Renders one asset tile. The picker lists documents and images side by side, so a thumbnail is
 * attempted only for image assets — formats no browser can decode (TIFF, HEIC) resolve to a
 * server-generated thumbnail — and everything else, plus any decode failure, falls back to the
 * icon-and-title tile rather than the browser's broken-image glyph.
 */
function AssetThumb({ asset, isSelected, onSelect }: AssetThumbProps) {
  const previewUrl = useAssetPreviewUrl(asset._id, asset.fileType, isImageMimeType(asset.fileType) ? asset.fileUrl ?? null : null);
  const [previewError, setPreviewError] = useState(false);

  useEffect(() => {
    setPreviewError(false);
  }, [previewUrl]);

  const showThumbnail = Boolean(previewUrl) && !previewError;

  return (
    <div className={`asset-style-thumb${isSelected ? ' asset-style-thumb--selected' : ''}`} onClick={() => onSelect(asset)} title={asset.title}>
      {showThumbnail ? (
        <img src={previewUrl as string} alt={asset.title} className="asset-style-thumb-img" onError={() => setPreviewError(true)} />
      ) : (
        <>
          <i className="icon-image asset-style-thumb-icon" aria-hidden="true" />
          {asset.title && <span className="asset-style-thumb-title">{asset.title}</span>}
        </>
      )}
    </div>
  );
}

interface SelectedPreviewProps {
  file: (File & { preview: string }) | null;
  asset: ExistingAssetRef | null;
}

/**
 * Preview of the current selection inside the dropzone. An image shows as a thumbnail — a dropped
 * file from its object URL, a library pick from the same resolver the asset grid uses, so formats
 * the browser cannot decode (TIFF, HEIC) still resolve to a server-generated thumbnail. Anything
 * else, and any decode failure, falls back to the icon-and-name tile.
 */
function SelectedPreview({ file, asset }: SelectedPreviewProps) {
  const assetPreviewUrl = useAssetPreviewUrl(asset?.id, asset?.mimetype, isImageMimeType(asset?.mimetype) ? asset?.key ?? null : null);
  const [previewError, setPreviewError] = useState(false);
  const filePreviewUrl = file && isImageMimeType(file.type) ? file.preview : null;
  const previewUrl = file ? filePreviewUrl : assetPreviewUrl;
  const name = file ? file.name : asset?.fileName ?? '';

  useEffect(() => {
    setPreviewError(false);
  }, [previewUrl]);

  return (
    <div className="asset-style-preview">
      {previewUrl && !previewError ? (
        <img src={previewUrl} alt={name} className="asset-style-preview-img" onError={() => setPreviewError(true)} />
      ) : (
        <i className="icon-image asset-style-preview-icon" aria-hidden="true" />
      )}
      <span className="asset-style-preview-name">{name}</span>
      {file && <span className="asset-style-preview-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>}
    </div>
  );
}

/**
 * Drop-in replacement for UploadDocuments with a PdfUploadModal-style dropzone/preview.
 * Same props, validation and ref-exposed selectedDocument() contract as UploadDocuments.
 */
function UploadDocumentsAssetStyle(
  {
    handleUpload,
    module,
    fromLibrary,
    onSelectedGradingGroupChange,
    setShowGradingGroupError,
    fromGrading,
    documentTypeList,
    hideName,
    accept,
    productId,
    fetchDocuments,
    showDropZone = true,
    isRenewMode,
    expairyDate,
    renewName,
    status,
    isSupplierLoggedIn,
    isProductArchived,
    validateFile,
    bottomBarContent,
    showAddToAssetLibrary,
    editId,
    editData,
  }: UploadDocumentsAssetStyleProps,
  ref: ForwardedRef<UploadDocumentsAssetStyleHandle>,
) {
  const { t } = useTranslation('assetLibraryAssets');
  const isTesting = module === 'testing';
  const isDesignSupportingDoc = module === 'designSupportingDoc';
  /** Design/BOM supporting documents upload through the resumable multipart engine, which has no size cap. */
  const skipsSizeLimit = module === 'designSupportingDoc' || module === 'bomDoc';
  const locationsUsed = getLocationsUsedForModule(module);
  const acceptedExtensions = useMemo(() => parseAcceptedExtensions(accept), [accept]);

  const [error, setError] = useState<ErrorState>({
    fileName: '',
    document: '',
    sizeError: '',
    typeError: '',
    documentType: '',
    expairyDueDate: '',
  });
  const [files, setFiles] = useState<(File & { preview: string })[]>([]);
  const [gradingOptions, setGradingOptions] = useState<GradingGroupOption[]>([]);
  const [selectedGradingGroup, setSelectedGradingGroup] = useState<GradingGroupOption[]>([]);
  const [assetSearch, setAssetSearch] = useState('');
  const [assets, setAssets] = useState<Asset[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsLoadingMore, setAssetsLoadingMore] = useState(false);
  const [assetPage, setAssetPage] = useState(1);
  const [hasMoreAssets, setHasMoreAssets] = useState(false);
  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const assetRequestIdRef = useRef(0);
  const fetchedAssetCountRef = useRef(0);
  const reducer = useSelector((reducerState: UploadDocumentsAssetStyleRootState) => reducerState);
  const { BOMConfigDetails } = reducer;
  const { productDetails } = BOMConfigDetails;
  const [supportingDocuments, setSupportingDocuments] = useState<SupportingDocumentsState>({
    fileName: '',
    fileExtn: '',
    document: null,
    existingAsset: null,
    documentType: null,
    expairyDueDate: null,
    changeStatus: true,
    addToDesign: false,
    addToAssetLibrary: false,
  });

  const onFileDrop = (acceptedFiles: File[]) => {
    const droppedFile = acceptedFiles?.[0];
    if (droppedFile) {
      setError((state) => ({
        ...state,
        document: '',
        typeError: '',
      }));
      if (validateFile) {
        const result = validateFile(droppedFile);
        if (!result.valid) {
          setError((state) => ({
            ...state,
            typeError: result.errors.join(', '),
          }));
          setFiles([]);
          setSupportingDocuments((doc) => ({
            ...doc,
            document: null,
            existingAsset: null,
            fileExtn: '',
          }));
          return;
        }
      }
      const fileSize = Number((droppedFile.size / 1024 / 1024).toFixed(0));
      let maxFileSize = 20;
      if (module === 'specificationDoc') {
        maxFileSize = 5;
      }
      if (!skipsSizeLimit && fileSize > maxFileSize) {
        setError((state) => ({
          ...state,
          sizeError: t('UPLOAD_DOCUMENTS.SIZE_ERROR_MESSAGE'),
        }));
        setFiles([]);
        setSupportingDocuments((doc) => ({
          ...doc,
          document: null,
          existingAsset: null,
          fileExtn: '',
        }));
      } else {
        setError((state) => ({
          ...state,
          document: '',
          sizeError: '',
        }));
        const rawFileName = droppedFile.name.split('.').slice(0, -1).join('.');
        const fileName = isTesting ? trimPdfTitle(rawFileName) : rawFileName;
        const fileExtn = droppedFile.name.substr(droppedFile.name.lastIndexOf('.') + 1);

        setSupportingDocuments((doc) => ({
          ...doc,
          document: droppedFile,
          existingAsset: null,
          fileName,
          fileExtn,
        }));
        setFiles(
          acceptedFiles.map((file) =>
            Object.assign(file, {
              preview: URL.createObjectURL(file),
            }),
          ),
        );
        setSelectedAssetId(null);
        if (module === 'specDoc') handleUpload?.();
      }
    } else {
      setError((state) => ({
        ...state,
        typeError: t('UPLOAD_DOCUMENTS.FILE_ERROR_MESSAGE'),
      }));
      if (fromLibrary && error.document) {
        setError((state) => ({
          ...state,
          document: '',
        }));
      }
    }
  };

  const handleSelectChange = (selectedList: GradingGroupOption[]) => {
    if (selectedList?.length === 0) {
      setSelectedGradingGroup([]);
    } else {
      setShowGradingGroupError(false);
      setSelectedGradingGroup(selectedList);
    }
  };

  useEffect(() => {
    if (gradingOptions?.length === 1) {
      handleSelectChange(gradingOptions);
    } else {
      const values = gradingOptions?.filter(
        (val) => !isEmpty(gradingOptions) && selectedGradingGroup?.length && selectedGradingGroup?.some((group) => group.value === val?.value),
      );
      handleSelectChange(values);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradingOptions]);

  useEffect(() => {
    let gradOptions = productDetails?.gradingGroups?.map((ele: GradingGroupOption) => {
      ele._id = ele.value;
      return ele;
    });
    gradOptions = gradOptions?.length ? gradOptions : [];
    setGradingOptions(gradOptions);
  }, [productDetails]);

  useEffect(
    () => () => {
      files.forEach((file) => URL.revokeObjectURL(file.preview));
    },
    [files],
  );

  useEffect(() => {
    if (isTesting && expairyDate) {
      setSupportingDocuments((doc) => ({ ...doc, expairyDueDate: new Date(expairyDate) }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expairyDate]);

  useEffect(() => {
    if (isTesting) {
      setSupportingDocuments((doc) => ({ ...doc, fileName: renewName ? trimPdfTitle(renewName) : '' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRenewMode]);

  useEffect(() => {
    if (isDesignSupportingDoc && editId) {
      setSupportingDocuments((doc) => ({
        ...doc,
        fileName: editData?.documentName ?? '',
        addToDesign: editData?.addToDesign ?? false,
        expairyDueDate: editData?.expiredOn ? new Date(editData.expiredOn) : null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, editData]);

  /**
   * Load one page of library assets. Page 1 replaces the list, later pages append to it.
   * A request id guards against an earlier in-flight page overwriting a newer search.
   * `hasMore` is derived from the raw (unfiltered) fetched count against the server total, because
   * the extension filter can shrink a page without meaning the server ran out of rows.
   */
  const fetchAssetPage = useCallback(
    async (pageToLoad: number) => {
      const isFirstPage = pageToLoad === 1;
      const requestId = ++assetRequestIdRef.current;
      if (isFirstPage) setAssetsLoading(true);
      else setAssetsLoadingMore(true);
      try {
        const res = fetchDocuments
          ? await fetchDocuments(assetSearch, pageToLoad, DOCUMENT_PICKER_PAGE_SIZE, accept)
          : await getDocumentsByLocation(productId!, assetSearch, accept, locationsUsed, pageToLoad, DOCUMENT_PICKER_PAGE_SIZE);
        if (requestId !== assetRequestIdRef.current) return;
        const fetchedAssets: Asset[] = res?.data?.assets ?? [];
        const total = Number(res?.data?.total);
        const filteredAssets = fetchedAssets.filter((asset) => matchesAcceptedExtensions(asset, acceptedExtensions));
        fetchedAssetCountRef.current = isFirstPage ? fetchedAssets.length : fetchedAssetCountRef.current + fetchedAssets.length;
        setAssets((prev) => (isFirstPage ? filteredAssets : [...prev, ...filteredAssets]));
        setAssetPage(pageToLoad);
        const hasTotal = Number.isFinite(total) && total > 0;
        setHasMoreAssets(fetchedAssets.length === DOCUMENT_PICKER_PAGE_SIZE && (!hasTotal || fetchedAssetCountRef.current < total));
      } catch (err) {
        if (requestId !== assetRequestIdRef.current) return;
        logError('UploadDocumentsAssetStyle: asset search failed:', err);
        if (isFirstPage) setAssets([]);
        setHasMoreAssets(false);
      } finally {
        if (requestId === assetRequestIdRef.current) {
          setAssetsLoading(false);
          setAssetsLoadingMore(false);
        }
      }
    },
    [assetSearch, productId, fetchDocuments, accept, acceptedExtensions, locationsUsed],
  );

  useEffect(() => {
    if (!showDropZone) return undefined;
    if (!fetchDocuments && !productId) {
      setAssets([]);
      setHasMoreAssets(false);
      return undefined;
    }
    const timer = setTimeout(() => fetchAssetPage(1), assetSearch.trim() ? 500 : 0);
    return () => clearTimeout(timer);
  }, [showDropZone, assetSearch, productId, fetchDocuments, fetchAssetPage]);

  /** Load the next page once the results list is scrolled near its end. */
  const handleAssetScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!hasMoreAssets || assetsLoading || assetsLoadingMore) return;
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight <= 40) fetchAssetPage(assetPage + 1);
  };

  const handleAssetSelect = (asset: Asset) => {
    if (asset._id === selectedAssetId) {
      setSelectedAssetId(null);
      setSupportingDocuments((doc) => ({ ...doc, existingAsset: null }));
      return;
    }
    if (!asset.fileUrl) return;
    setSelectedAssetId(asset._id);
    setError((state) => ({
      ...state,
      document: '',
      sizeError: '',
      typeError: '',
    }));
    const fileExtn = asset.fileUrl.split('.').pop()?.toLowerCase() ?? '';
    const fileName = asset.title ?? '';
    setFiles([]);
    setSupportingDocuments((doc) => ({
      ...doc,
      document: null,
      existingAsset: { id: asset._id, key: asset.fileUrl as string, mimetype: asset.fileType ?? '', fileName, size: asset.size ?? 0 },
      fileName,
      fileExtn,
      /** A file picked from the library is already an asset, so it is never filed a second time. */
      addToAssetLibrary: false,
    }));
  };

  const validateTestingTitle = (): string => {
    const trimmedTitle = supportingDocuments.fileName.replace(/\s/g, '');
    if (!trimmedTitle.length) return t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.PDF_TITLE_REQUIRED');
    if (supportingDocuments.fileName.length > PDF_TITLE_MAX_LENGTH) return t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.PDF_TITLE_MAX_LENGTH');
    if (!/^[\w\-\s]+$/.test(supportingDocuments.fileName)) return t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.SPECIAL_CHARACTER_NOT_ALLOWED');
    return '';
  };

  const validateForm = () => {
    let formIsValid = true;
    const errors: Partial<ErrorState> = { fileName: '', document: '' };
    if (isTesting) {
      const titleError = validateTestingTitle();
      if (titleError) {
        formIsValid = false;
        errors.fileName = titleError;
      }
    } else if (!supportingDocuments.fileName.replace(/\s/g, '')?.length) {
      formIsValid = false;
      errors.fileName = t('UPLOAD_DOCUMENTS.SELECTED_DOCUMENT_TITLE');
      errors.sizeError = '';
    }
    if (showDropZone && !editId && !supportingDocuments.document && !supportingDocuments.existingAsset) {
      formIsValid = false;
      errors.document = isTesting ? t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.FILE_REQUIRED') : t('UPLOAD_DOCUMENTS.SELECTED_DOCUMENT_FILE');
      errors.sizeError = '';
    }
    if (!supportingDocuments.documentType && module === 'specificationDoc') {
      formIsValid = false;
      errors.documentType = t('UPLOAD_DOCUMENTS.PLEASE_SELECT_DOCUMENT_TYPE');
      errors.sizeError = '';
    }
    setError((state) => ({
      ...state,
      ...errors,
    }));
    return formIsValid;
  };

  useEffect(() => {
    if (module === 'specificationDoc') {
      if (selectedGradingGroup?.length > 0) {
        onSelectedGradingGroupChange(selectedGradingGroup);
      } else {
        onSelectedGradingGroupChange([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGradingGroup]);

  /**
   * The selection as the host should send it. The asset-library tick only travels when the host
   * asked for the checkbox AND the file came from the device, so a stale tick — or one set before
   * the user switched to a library pick — can never reach the payload.
   */
  const buildSelectedDocument = (): SupportingDocumentsState => ({
    ...supportingDocuments,
    addToAssetLibrary: showAddToAssetLibrary === true && supportingDocuments.addToAssetLibrary && Boolean(supportingDocuments.document),
  });

  useImperativeHandle(ref, () => ({
    selectedDocument() {
      // eslint-disable-next-line sonarjs/no-collapsible-if
      if (fromLibrary && !error.typeError) {
        if (validateForm()) {
          return buildSelectedDocument();
        }
      } else if (!fromLibrary && validateForm()) {
        return buildSelectedDocument();
      }

      if (module === 'specificationDoc') {
        if (validateForm()) {
          return buildSelectedDocument();
        }
      } else if (!fromLibrary && validateForm()) {
        return buildSelectedDocument();
      }
      return undefined;
    },
  }));

  const handleChange = (event: ChangeEvent<HTMLInputElement> | DocumentTypeOption, fieldName: string) => {
    setSupportingDocuments({
      ...supportingDocuments,
      [fieldName]: fieldName === 'documentType' ? event : (event as ChangeEvent<HTMLInputElement>)?.currentTarget?.value,
    });
    setError((state) => ({
      ...state,
      [fieldName]: '',
    }));
  };

  const handleExpairyDateChange = (date: Date | null) => {
    setSupportingDocuments((doc) => ({ ...doc, expairyDueDate: date }));
    setError((state) => ({ ...state, expairyDueDate: '' }));
  };

  const handleChangeStatusToggle = () => {
    setSupportingDocuments((doc) => ({ ...doc, changeStatus: !doc.changeStatus }));
  };

  const handleAddToDesignToggle = () => {
    setSupportingDocuments((doc) => ({ ...doc, addToDesign: !doc.addToDesign }));
  };

  const handleAddToAssetLibraryToggle = () => {
    setSupportingDocuments((doc) => ({ ...doc, addToAssetLibrary: !doc.addToAssetLibrary }));
  };

  const message = fromGrading ? t('UPLOAD_DOCUMENTS.DRAG_DROP_XLSX') : t('UPLOAD_DOCUMENTS.DRAG_AND_DROP_MESSAGE');
  /**
   * Idle dropzone prompt. Every flow but the grading import shows the two-part prompt from the
   * design, whose leading phrase is painted as a link. The dropzone itself already opens the file
   * picker, so that phrase carries no handler of its own.
   */
  const idlePrompt = fromGrading ? (
    message
  ) : (
    <>
      <span className="asset-style-hint-action">{t('assets.clickToUpload')}</span> {t('assets.orDragAndDrop')}
    </>
  );
  const selectedFile = files[0] ?? null;
  const showChangeStatusCheckbox = isTesting && Boolean(isRenewMode) && computeShowChangeStatusCheckbox(status) && !isSupplierLoggedIn && !isProductArchived;
  const NON_STANDARD_EXTENSIONS = ['dxf', 'rul', 'wds'];
  const showAddToDesignCheckbox = isDesignSupportingDoc && !NON_STANDARD_EXTENSIONS.includes(supportingDocuments.fileExtn?.toLowerCase());

  return (
    <form noValidate>
      <div className="common-image-upload new-image-upload">
        {showDropZone ? (
          <Dropzone accept={accept ?? ''} onDrop={onFileDrop}>
            {({ getRootProps, getInputProps, isDragActive, open }) => (
              <>
                <div className="asset-style-body">
                  <div className="asset-style-left-panel">
                    <div className="asset-style-dropzone-wrap">
                      <section className={`asset-style-dropzone${isDragActive ? ' asset-style-dropzone-active' : ''}`} {...getRootProps()}>
                        <input {...getInputProps()} />
                        {selectedFile || supportingDocuments.existingAsset ? (
                          <SelectedPreview file={selectedFile} asset={supportingDocuments.existingAsset} />
                        ) : (
                          <div className="asset-style-empty">
                            <FileIcon size={72} className="asset-style-empty-icon" />
                            <p className="asset-style-hint">{isDragActive ? t('UPLOAD_DOCUMENTS.DROP_FILES_HERE') : idlePrompt}</p>
                            {accept && (
                              <p className="asset-style-subhint">
                                <span className="asset-style-subhint-label">{t('assets.allowedFileExtension')}</span> {accept}
                              </p>
                            )}
                          </div>
                        )}
                      </section>
                    </div>
                    {Object.keys(error)?.length !== 0 && (
                      <>
                        {error?.typeError && (
                          <div className="d-flex align-items-center justify-content-center">
                            <span className="error-message text-center w-50">{error.typeError}</span>
                          </div>
                        )}
                        {error?.document && (
                          <div className="d-flex align-items-center justify-content-center">
                            <span className="error-message text-center w-50">{error.document}</span>
                          </div>
                        )}
                        {error?.sizeError && (
                          <div className="d-flex align-items-center justify-content-center">
                            <span className="error-message text-center w-50">{error.sizeError}</span>
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  <div className="asset-style-right-panel">
                    {(fetchDocuments || productId) && (
                      <>
                        <div className="asset-style-search-wrap">
                          <input
                            type="text"
                            className="form-control form-control-sm asset-style-search-input"
                            placeholder={t('assets.searchAssetLibrary')}
                            value={assetSearch}
                            onChange={(e) => setAssetSearch(e.target.value)}
                          />
                          <i className="bombiix-icon-search asset-style-search-icon" aria-hidden="true" />
                        </div>
                        <div className="asset-style-asset-results" onScroll={handleAssetScroll}>
                          {assetsLoading ? (
                            <p className="asset-style-hint">{t('assets.loading')}</p>
                          ) : assets.length === 0 ? (
                            <p className="asset-style-hint">{t('assets.noDocumentsFound')}</p>
                          ) : (
                            <>
                              <div className="asset-style-asset-grid">
                                {assets.map((asset) => (
                                  <AssetThumb key={asset._id} asset={asset} isSelected={selectedAssetId === asset._id} onSelect={handleAssetSelect} />
                                ))}
                              </div>
                              {assetsLoadingMore && <p className="asset-style-hint">{t('assets.loading')}</p>}
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="asset-style-bottom-bar">
                  <button type="button" className="bombiix-btn btn-blue" onClick={open}>
                    {t('UPLOAD_DOCUMENTS.UPLOAD_BUTTON')}
                  </button>

                  {!hideName && (
                    <div className="asset-style-bottom-field">
                      <NewInput
                        type="text"
                        name="fileName"
                        value={supportingDocuments.fileName}
                        onChange={(e) => handleChange(e, 'fileName')}
                        placeholder={t('UPLOAD_DOCUMENTS.DOCUMENT_TITLE_PLACEHOLDER')}
                        invalid={!!error.fileName}
                      />
                      <span className="error-message">{error.fileName}</span>
                    </div>
                  )}

                  {isDesignSupportingDoc && (
                    <div className="asset-style-bottom-field asset-style-testing-date-field">
                      <NewDatePicker
                        dateFormat="dd/MM/yyyy"
                        placeholder={t('TAKING_DATES.SELECT_EXPIRY_DATE')}
                        filterDate={(date: Date) => isTodayOrFutureDate(date)}
                        value={supportingDocuments.expairyDueDate}
                        onChange={handleExpairyDateChange}
                        invalid={!!error.expairyDueDate}
                      />
                      {error.expairyDueDate && <small className="error-message">{error.expairyDueDate}</small>}
                    </div>
                  )}

                  {module === 'specificationDoc' && (
                    <>
                      <div className="asset-style-bottom-field">
                        <NewInput
                          type="text"
                          name="fileName"
                          value={supportingDocuments?.fileName}
                          onChange={(e) => handleChange(e, 'fileName')}
                          placeholder={t('SPEC_TEMP_LIBRARY.COL_DOC_TITLE')}
                          invalid={!!error?.fileName}
                        />
                        <span className="error-message">{error?.fileName}</span>
                      </div>
                      <div className="asset-style-bottom-field asset-style-doctype-field">
                        <NewSelectMultiSelect<DocumentTypeOption>
                          options={documentTypeList?.filter((item) => item?.isActive) ?? []}
                          getOptionLabel={(option: DocumentTypeOption) => `${option.name}`}
                          getOptionValue={(option: DocumentTypeOption) => `${option._id}`}
                          value={supportingDocuments?.documentType}
                          onChange={(option) => handleChange(option as DocumentTypeOption, 'documentType')}
                          placeholder={t('SPEC_TEMP_LIBRARY.COL_DOC_TYPE')}
                          menuPlacement="auto"
                          invalid={!!error?.documentType}
                        />
                        <span className="error-message">{error?.documentType}</span>
                      </div>
                    </>
                  )}

                  {isTesting && (
                    <>
                      <div className="asset-style-bottom-field">
                        <NewInput
                          type="text"
                          name="fileName"
                          placeholder={t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.PDF_TITLE')}
                          value={supportingDocuments.fileName}
                          maxLength={PDF_TITLE_MAX_LENGTH}
                          onChange={(e) => handleChange(e, 'fileName')}
                          invalid={!!error.fileName}
                        />
                        <div className="character-counter">
                          {supportingDocuments.fileName.length}/{PDF_TITLE_MAX_LENGTH}
                        </div>
                        {error.fileName && <small className="error-message">{error.fileName}</small>}
                      </div>
                      <div className="asset-style-bottom-field asset-style-testing-date-field">
                        <NewDatePicker
                          dateFormat="dd/MM/yyyy"
                          placeholder={t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.EXPIRY_DATE')}
                          filterDate={(date: Date) => isTodayOrFutureDate(date)}
                          value={supportingDocuments.expairyDueDate}
                          onChange={handleExpairyDateChange}
                          invalid={!!error.expairyDueDate}
                        />
                        {error.expairyDueDate && <small className="error-message">{error.expairyDueDate}</small>}
                      </div>
                    </>
                  )}

                  {bottomBarContent}

                  {isTesting && showChangeStatusCheckbox && (
                    <div className="asset-style-bottom-checkbox">
                      <NewCheckbox
                        id="changeStatus"
                        name="changeStatus"
                        label={t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.CLEAR_STATUS')}
                        checked={supportingDocuments.changeStatus}
                        onChange={handleChangeStatusToggle}
                      />
                    </div>
                  )}

                  {showAddToDesignCheckbox && (
                    <div className="asset-style-bottom-checkbox">
                      <NewCheckbox
                        id="addToDesignDetail"
                        name="addToDesignDetail"
                        label={t('assets.addToDesignShort')}
                        checked={supportingDocuments.addToDesign}
                        onChange={handleAddToDesignToggle}
                        disabled={!supportingDocuments.document && !supportingDocuments.existingAsset && !editId}
                      />
                    </div>
                  )}

                  {showAddToAssetLibrary && (
                    <div className="asset-style-bottom-checkbox">
                      <NewCheckbox
                        id="addToAssetLibrary"
                        name="addToAssetLibrary"
                        label={t('assets.addToAssetLibrary')}
                        checked={supportingDocuments.addToAssetLibrary && Boolean(supportingDocuments.document)}
                        onChange={handleAddToAssetLibraryToggle}
                        disabled={!supportingDocuments.document}
                      />
                    </div>
                  )}
                </div>
              </>
            )}
          </Dropzone>
        ) : (
          <>
            {!hideName && (
              <div className="d-flex w-100 upload-text-data">
                <div className="w-100">
                  <NewInput
                    type="text"
                    name="fileName"
                    value={supportingDocuments.fileName}
                    onChange={(e) => handleChange(e, 'fileName')}
                    placeholder={t('UPLOAD_DOCUMENTS.DOCUMENT_TITLE_PLACEHOLDER')}
                    invalid={!!error.fileName}
                  />
                  <span className="error-message">{error.fileName}</span>
                </div>
              </div>
            )}

            {bottomBarContent}

            {module === 'specificationDoc' && (
              <Row className="mt-16">
                <Col md={6}>
                  <div className="w-100">
                    <NewInput
                      type="text"
                      name="fileName"
                      value={supportingDocuments?.fileName}
                      onChange={(e) => handleChange(e, 'fileName')}
                      placeholder={t('SPEC_TEMP_LIBRARY.COL_DOC_TITLE')}
                      invalid={!!error?.fileName}
                    />
                    <span className="error-message">{error?.fileName}</span>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="w-100">
                    <NewSelectMultiSelect<DocumentTypeOption>
                      options={documentTypeList?.filter((item) => item?.isActive) ?? []}
                      getOptionLabel={(option: DocumentTypeOption) => `${option.name}`}
                      getOptionValue={(option: DocumentTypeOption) => `${option._id}`}
                      value={supportingDocuments?.documentType}
                      onChange={(option) => handleChange(option as DocumentTypeOption, 'documentType')}
                      placeholder={t('SPEC_TEMP_LIBRARY.COL_DOC_TYPE')}
                      menuPlacement="auto"
                      invalid={!!error?.documentType}
                    />
                    <span className="error-message">{error?.documentType}</span>
                  </div>
                </Col>
              </Row>
            )}

            {isTesting && (
              <>
                <Row className="mt-24">
                  <Col lg="8">
                    <div className="w-100">
                      <NewInput
                        type="text"
                        name="fileName"
                        placeholder={t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.PDF_TITLE')}
                        value={supportingDocuments.fileName}
                        maxLength={PDF_TITLE_MAX_LENGTH}
                        onChange={(e) => handleChange(e, 'fileName')}
                        invalid={!!error.fileName}
                      />
                      <div className="character-counter">
                        {supportingDocuments.fileName.length}/{PDF_TITLE_MAX_LENGTH}
                      </div>
                      {error.fileName && <small className="error-message">{error.fileName}</small>}
                    </div>
                  </Col>
                  <Col lg="4">
                    <div className="asset-style-testing-date-field">
                      <NewDatePicker
                        dateFormat="dd/MM/yyyy"
                        placeholder={t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.EXPIRY_DATE')}
                        filterDate={(date: Date) => isTodayOrFutureDate(date)}
                        value={supportingDocuments.expairyDueDate}
                        onChange={handleExpairyDateChange}
                        invalid={!!error.expairyDueDate}
                      />
                      {error.expairyDueDate && <small className="error-message">{error.expairyDueDate}</small>}
                    </div>
                  </Col>
                </Row>
                {showChangeStatusCheckbox && (
                  <div className="checkbox-label">
                    <input type="checkbox" id="changeStatus" checked={supportingDocuments.changeStatus} onClick={handleChangeStatusToggle} readOnly />
                    <label htmlFor="changeStatus" className="chklabel">
                      <span>{t('DEVLOPMENT_ORDER.TESTING.UPLOAD_TESTING.CLEAR_STATUS')}</span>
                    </label>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </form>
  );
}

const ForwardedUploadDocumentsAssetStyle = forwardRef(UploadDocumentsAssetStyle);
ForwardedUploadDocumentsAssetStyle.displayName = 'UploadDocumentsAssetStyle';

export default ForwardedUploadDocumentsAssetStyle;
